import React, { useEffect, useMemo, useState } from 'react'
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Combobox, ComboboxItem, Icon, Input } from '@node-space/ui-components'
import { useRoutingNumberValidation } from 'hooks/queries/Beneficiaries/useRoutingNumberValidation'
import { useIbanValidation } from 'hooks/queries/useIbanValidation'
import { useScanValidation } from 'hooks/queries/useScanValidation'
import { useSouthAfricanBanks } from 'hooks/queries/useSouthAfricanBanks'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useBankDetailValidation } from 'pages/Contacts/hooks/useBankDetailsValidation'
import { useFormFieldOptions } from 'pages/Contacts/hooks/useFiatFormFieldOptions'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import {
  FieldsFetchedFromIbanValidation,
  FormFieldLabels,
} from 'pages/Contacts/types/fiatInstruments'
import { getIsValidIban } from 'pages/Contacts/utils/fiatInstruments'
import { BeneficiaryDetails, BeneficiaryDetailsCurrency } from 'types/beneficiaries'
import { titleCase } from 'utils/utils'

type BankDetailsProps = {
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  isBusinessAccount: boolean
  formFieldLabels: FormFieldLabels
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  trigger: UseFormTrigger<ContactFiatInstrumentFormData>
  setError: UseFormSetError<ContactFiatInstrumentFormData>
  contactBeneficiary?: BeneficiaryDetails
}

export const LocalBankDetails = ({
  formFieldLabels,
  formValues,
  errors,
  clearErrors,
  register,
  setValue,
  trigger,
  setError,
  contactBeneficiary,
}: BankDetailsProps) => {
  const { t } = useTranslation()

  const [isValidIban, setIsValidIban] = useState<boolean>(false)
  const [isValidScan, setIsValidScan] = useState<boolean>(false)
  const [isValidRoutingNumber, setIsValidRoutingNumber] = useState<boolean>(false)

  const { accountTypeOptions } = useFormFieldOptions({ formValues })

  const isUsd = formValues?.currency === BeneficiaryDetailsCurrency.USD
  const isZar = formValues?.currency === BeneficiaryDetailsCurrency.ZAR

  const { setRequestError } = useMappedErrorMessage()

  const isIbanCurrency = formValues?.currency === BeneficiaryDetailsCurrency.EUR

  const {
    refetch: validateIban,
    isFetching: isValidatingIban,
    isError: isValidatingIbanError,
    data: ibanBankDetails,
  } = useIbanValidation(formValues?.accountNumber, 'IBAN', setRequestError, false)

  const {
    refetch: validateScan,
    isFetching: isValidatingScan,
    isError: isValidatingScanError,
    data: scanBankDetails,
  } = useScanValidation(formValues?.bankCode, formValues?.accountNumber, setRequestError, false)

  const {
    refetch: validateRoutingNumber,
    isFetching: isValidatingRoutingNumber,
    isError: isValidatingRoutingNumberError,
    data: routingNumberBankDetails,
  } = useRoutingNumberValidation(formValues?.bankCode, setRequestError, false)

  const {
    data: southAfricanBanks,
    isFetching: isFetchingSouthAfricanBanks,
    isError: isSouthAfricanBanksError,
  } = useSouthAfricanBanks(setRequestError, isZar)

  const isBankDetailsValidationLoading = isValidatingIban || isValidatingScan

  const scanSupportedPaymentMethods = scanBankDetails?.supportedPaymentMethods
  const isGBP = formValues?.currency === BeneficiaryDetailsCurrency.GBP

  useBankDetailValidation({
    formValues,
    setValue,
    setError,
    clearErrors,
    ibanValidationProps: {
      isValidatingIbanError,
      ibanBankDetails,
      setIsValidIban,
      setIbanBankDetailsData: (details: FieldsFetchedFromIbanValidation) => {
        setValue('bankCode', details?.bankCode)
      },
    },
    routingNumberValidationProps: {
      isValidatingRoutingNumberError,
      routingNumberBankDetails,
      setIsRoutingNumberValid: setIsValidRoutingNumber,
    },
    scanValidationProps: {
      isValidatingScanError,
      scanBankDetails,
      setIsValidScan: setIsValidScan,
    },
    contactBeneficiary,
  })

  useEffect(() => {
    if (contactBeneficiary) return
    const isFPSandChapsSupported =
      scanSupportedPaymentMethods?.includes('CHAPS') ||
      scanSupportedPaymentMethods?.includes('BACS') ||
      scanSupportedPaymentMethods?.includes('FASTER_PAYMENT') ||
      scanSupportedPaymentMethods?.includes('CCC_PAYMENTS')

    if (scanBankDetails && !isFPSandChapsSupported && isGBP) {
      setError('accountNumber', { message: t('reachabilityCheckErrorMessage') })
      setError('bankCode', { message: t('reachabilityCheckErrorMessage') })
    }
  }, [scanBankDetails, contactBeneficiary])

  useEffect(() => {
    if (contactBeneficiary) return
    if (formValues?.currency !== BeneficiaryDetailsCurrency.EUR) return

    clearErrors(['accountNumber'])

    if (!isBankDetailsValidationLoading && ibanBankDetails) {
      const isValidIban = getIsValidIban({
        currency: formValues?.currency,
        ibanBankDetails,
      })
      setIsValidIban(isValidIban)

      if (!isValidIban) {
        setError('accountNumber', { message: t('ibanValidationError') })
        setValue('bankCode', '')
        return
      }
      setValue('bankCode', ibanBankDetails?.bankAccount?.bankCode)
    }
  }, [ibanBankDetails, isValidatingIbanError, isBankDetailsValidationLoading, contactBeneficiary])

  useEffect(() => {
    if (contactBeneficiary) return
    if (formValues?.currency !== BeneficiaryDetailsCurrency.USD) return
    if (!routingNumberBankDetails) return

    const { bankDetails, validationFailed } = routingNumberBankDetails ?? {}
    const { bankAddress } = bankDetails ?? {}

    const isValid = !!bankAddress && !validationFailed?.length
    setIsValidRoutingNumber(isValid)

    if (!isValid) {
      setError('bankCode', { message: t('abaRoutingNumberError') })
      resetBankAddress()
    } else {
      const { addressLine1, addressLine2, city, region, postCode } = bankAddress || {}
      setValue('beneficiaryAddressLine1', addressLine1)
      setValue('beneficiaryAddressLine2', addressLine2)
      setValue('beneficiaryCity', city)
      setValue('beneficiaryRegion', region)
      setValue('beneficiaryPostCode', postCode)
    }
  }, [routingNumberBankDetails, formValues?.currency, contactBeneficiary])

  useEffect(() => {
    clearErrors()
  }, [formValues?.currency, clearErrors])

  useEffect(() => {
    if (!formValues?.bankCode) {
      setValue('bankName', '')
    }
  }, [formValues?.bankCode])

  const onLocalFormFieldUpdate =
    (fieldName: keyof ContactFiatInstrumentFormData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(fieldName, e.target.value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

  const onAccountTypeChange = (value: string) => {
    setValue('accountType', value)
  }

  const handleAccountNumberOnBlur = async () => {
    if (contactBeneficiary) return
    clearErrors()

    const isValid = await trigger('accountNumber')

    if (
      formValues?.currency === BeneficiaryDetailsCurrency.GBP &&
      !!formValues?.bankCode &&
      isValid
    ) {
      validateScan()
      return
    }

    if (isValid && isIbanCurrency) {
      validateIban()
    }
  }

  const handleSortCodeOnBlur = async () => {
    if (contactBeneficiary) return
    if (!formValues?.bankCode) return

    const isAccountNumberAndBankCodeValid = await trigger(['accountNumber', 'bankCode'])
    setIsValidScan(isAccountNumberAndBankCodeValid)

    if (isAccountNumberAndBankCodeValid) {
      validateScan()
    }
  }

  const handleRoutingNumberOnBlur = async () => {
    if (contactBeneficiary) return
    if (formValues?.currency !== BeneficiaryDetailsCurrency.USD) return
    if (!formValues?.bankCode) return

    await validateRoutingNumber()
  }

  const handleBankCodeOnBlur = async () => {
    const isGBp = formValues?.currency === BeneficiaryDetailsCurrency.GBP
    if (contactBeneficiary) return
    clearErrors()

    if (!formValues?.bankCode) return

    if (isGBp) {
      const isScanValid = await trigger(['accountNumber', 'bankCode'])
      setIsValidScan(isScanValid)

      if (isScanValid) {
        handleSortCodeOnBlur()
      }
      return
    }

    const isValid = await trigger('bankCode')

    if (!isValid) return

    if (formValues?.currency === BeneficiaryDetailsCurrency.USD) {
      handleRoutingNumberOnBlur()
    }
  }

  const handleAccountNumberConfirmOnBlur = async () => {
    if (contactBeneficiary) return

    if (!formValues?.accountNumberConfirm) return

    clearErrors(['accountNumber', 'accountNumberConfirm'])

    if (formValues?.accountNumber && formValues?.accountNumberConfirm) {
      await trigger(['accountNumber', 'accountNumberConfirm'])
    }
  }

  const resetBankAddress = () => {
    setValue('bankName', '')
    setValue('bankAddressLine1', '')
    setValue('bankAddressLine2', '')
    setValue('bankCity', '')
    setValue('bankRegion', '')
    setValue('bankPostCode', '')
  }

  const isAccountNumberAndBankCodeInline =
    formValues?.currency === BeneficiaryDetailsCurrency.EUR ||
    formValues?.currency === BeneficiaryDetailsCurrency.GBP

  const southAfricanBankOptions: ComboboxItem[] = useMemo(() => {
    if (!southAfricanBanks?.length) return []
    return southAfricanBanks?.map(bank => ({
      leftText: titleCase(bank?.name),
      value: bank?.id?.toString(),
    }))
  }, [southAfricanBanks])

  useEffect(() => {
    if (isValidatingRoutingNumberError) {
      setError('bankCode', { message: t('abaRoutingNumberError') })
    }
  }, [isValidatingRoutingNumberError])

  const isValidAccountNumber = useMemo(() => {
    if (formValues?.currency === BeneficiaryDetailsCurrency.GBP) {
      return isValidScan
    }

    if (formValues?.currency === BeneficiaryDetailsCurrency.EUR) {
      return isValidIban
    }

    return false
  }, [isValidIban, isValidScan, formValues?.currency])

  const isValidBankCode = useMemo(() => {
    if (formValues?.currency === BeneficiaryDetailsCurrency.GBP) {
      return isValidScan
    }

    if (formValues?.currency === BeneficiaryDetailsCurrency.USD) {
      return isValidRoutingNumber
    }

    return false
  }, [isValidRoutingNumber, isValidScan, formValues?.currency])

  const handleBankOnChange = (bankCode: string) => {
    setValue('bankNameCode', bankCode)
    const bankName = southAfricanBankOptions?.find(bank => bank?.value === bankCode)?.leftText
    if (bankName) {
      setValue('bankName', bankName as string)
    }
  }

  const isAccountTypeRequired = isUsd || isZar

  return (
    <Box width="full" paddingT={12}>
      <h2 className="text-foreground-prominent text-h2">{t('formFields.bankDetails')}</h2>
      <Box flexCol width="full" gapY={16} paddingT={12}>
        <Box
          gapX={12}
          gapY={16}
          paddingB={12}
          width="full"
          flex={isAccountNumberAndBankCodeInline}
          flexCol={!isAccountNumberAndBankCodeInline}
        >
          {isZar && (
            <Box flexSize="fill">
              <Combobox
                {...register('bankNameCode')}
                label={t('bank')}
                id="bankNameCode"
                items={southAfricanBankOptions}
                onChange={handleBankOnChange}
                errorText={
                  errors?.bankNameCode?.message ??
                  (isSouthAfricanBanksError ? t('fiatSend.failedToLoadBanks') : undefined)
                }
                loading={isFetchingSouthAfricanBanks}
                defaultValue={formValues?.bankNameCode}
              />
            </Box>
          )}

          <Box
            {...(isAccountNumberAndBankCodeInline && { flexSize: 'auto', width: 'full', gapX: 12 })}
          >
            <Input
              {...register('accountNumber')}
              name="accountNumber"
              testId="accountNumber"
              label={formFieldLabels?.accountNumber}
              placeholder={formFieldLabels?.accountNumberPlaceholder}
              errorText={errors?.accountNumber?.message}
              error={!!errors?.accountNumber?.message}
              onBlur={handleAccountNumberOnBlur}
              onChange={onLocalFormFieldUpdate('accountNumber')}
              loading={isBankDetailsValidationLoading}
              IconButton={
                isValidAccountNumber &&
                formValues?.accountNumber && (
                  <Button variant="ghost" iconElement={<Icon name="Check" />} />
                )
              }
              iconPosition="right"
            />
          </Box>

          {isUsd && (
            <Box {...(isAccountNumberAndBankCodeInline && { flexSize: 'auto', width: 'full' })}>
              <Input
                {...register('accountNumberConfirm')}
                name="accountNumberConfirm"
                testId="accountNumberConfirm"
                label={t('formFields.confirmAccountNumber')}
                placeholder={t('formFields.placeholder.confirmAccountNumber')}
                errorText={errors?.accountNumberConfirm?.message}
                error={!!errors?.accountNumberConfirm?.message}
                onChange={onLocalFormFieldUpdate('accountNumberConfirm')}
                onBlur={handleAccountNumberConfirmOnBlur}
              />
            </Box>
          )}

          <Box {...(isAccountNumberAndBankCodeInline && { flexSize: 'auto', width: 'full' })}>
            <Input
              {...register('bankCode')}
              name="bankCode"
              testId="bankCode"
              label={formFieldLabels.bankCode}
              placeholder={formFieldLabels?.bankCodePlaceholder}
              errorText={errors?.bankCode?.message}
              error={!!errors?.bankCode?.message}
              loading={isValidatingRoutingNumber || isValidatingScan}
              onChange={onLocalFormFieldUpdate('bankCode')}
              onBlur={handleBankCodeOnBlur}
              IconButton={
                isValidBankCode &&
                !!formValues?.bankCode && (
                  <Button variant="ghost" iconElement={<Icon name="Check" />} />
                )
              }
              iconPosition="right"
            />
          </Box>
          {isAccountTypeRequired && (
            <Combobox
              {...register('accountType')}
              key={formValues?.accountType}
              id="accountType"
              testId="accountType"
              label={t('formFields.accountType')}
              items={accountTypeOptions}
              errorText={errors?.accountType?.message}
              defaultValue={formValues?.accountType}
              onChange={value => {
                onAccountTypeChange(value)
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}
